import { create } from "zustand"
import { useAuth } from "@clerk/react"

export const NIRA_BASE = "/api/v1/nira/v2"
export const GUEST_BASE = "/api/v1/guest/nira"

export type LoginReason = "second_search" | "landing_search" | "simulator" | "login_required" | "upload"

/** Sign in to an existing account, or create one. */
export type LoginMode = "in" | "up"

interface LoginGate {
  open: boolean
  reason: LoginReason | null
  /** Where to go once the visitor has signed in (omit to stay on the page). */
  after: string | null
  mode: LoginMode
  show: (reason: LoginReason, after?: string | null, mode?: LoginMode) => void
  close: () => void
}

export const useLoginGate = create<LoginGate>((set) => ({
  open: false,
  reason: null,
  after: null,
  mode: "in",
  show: (reason, after = null, mode = "in") => set({ open: true, reason, after, mode }),
  close: () => set({ open: false, reason: null, after: null, mode: "in" }),
}))

/** Open the login modal from anywhere (stores, actions, components). */
export function requireLogin(reason: LoginReason, after?: string | null, mode?: LoginMode): void {
  useLoginGate.getState().show(reason, after, mode)
}

/** `signedIn` plus the `requireLogin` opener, for components. */
export function useAuthGate() {
  const { isLoaded, isSignedIn } = useAuth()
  return { loaded: !!isLoaded, signedIn: !!isSignedIn, requireLogin }
}

const USED_KEY = "nira-guest-search-used"
const THREADS_KEY = "nira-guest-threads"
const QUESTION_KEY = "nira-guest-question"

// In-memory mirror: guest markers must still work when sessionStorage throws (private mode, blocked storage).
const mem = new Map<string, string>()
let signedInHint = false

function read(key: string): string | null {
  try {
    const v = window.sessionStorage.getItem(key)
    if (v !== null) return v
  } catch {
    /* fall back to memory */
  }
  return mem.get(key) ?? null
}

function write(key: string, value: string): void {
  mem.set(key, value)
  try {
    window.sessionStorage.setItem(key, value)
  } catch {
    /* memory copy still holds it for this page load */
  }
}

export const guestSearchUsed = (): boolean => read(USED_KEY) === "1"
export const markGuestSearchUsed = (): void => write(USED_KEY, "1")

function guestThreads(): string[] {
  try {
    const v: unknown = JSON.parse(read(THREADS_KEY) ?? "[]")
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []
  } catch {
    return []
  }
}

export const markGuestThread = (id: string): void => write(THREADS_KEY, JSON.stringify([...guestThreads(), id]))
export const rememberGuestQuestion = (q: string): void => write(QUESTION_KEY, q)
export const lastGuestQuestion = (): string => read(QUESTION_KEY) ?? ""

/** Guest mode only applies while signed out: a signed-in visitor never uses the guest base. */
export const isGuestThread = (id: string, signedIn: boolean = signedInHint): boolean =>
  !signedIn && guestThreads().includes(id)

/** API base for a thread: the guest base for threads this visitor made while signed out. */
export const baseFor = (threadId: string, signedIn: boolean = signedInHint): string =>
  isGuestThread(threadId, signedIn) ? GUEST_BASE : NIRA_BASE

/** Tell non-React code (stream actions) whether the visitor is signed in. */
export const setSignedInHint = (v: boolean): void => { signedInHint = v }

/** Forget every guest marker (storage and memory). Returns the guest's last question. */
export function clearGuestState(): string {
  const q = lastGuestQuestion()
  for (const k of [USED_KEY, THREADS_KEY, QUESTION_KEY]) {
    mem.delete(k)
    try {
      window.sessionStorage.removeItem(k)
    } catch {
      /* nothing stored */
    }
  }
  return q
}

/** True for the 401 `{"code":"login_required"}` a guest endpoint returns. */
export function isLoginRequired(err: unknown): boolean {
  const e = err as { status?: unknown; code?: unknown; detail?: unknown; message?: unknown } | null
  if (!e || e.status !== 401) return false
  if (e.code === "login_required") return true
  return [e.detail, e.message].some((t) => typeof t === "string" && t.includes("login_required"))
}
